/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("ChromeRewriterProvider", ["AIProviderBase"], function (context, cls) {
  /**
   * Chrome Rewriter provider (built-in local model).
   * @class ChromeRewriterProvider
   * @memberOf classes
   */
  class ChromeRewriterProvider extends cls.AIProviderBase /** @lends classes.ChromeRewriterProvider.prototype */ {
    constructor() {
      super("chrome-rewriter", "Chrome Rewriter", "chrome-local", { rewrite: true });

      /** @private */
      this._rewriter = null;
      /** @private */
      this._rewriterPromise = null;
      /** @private */
      this._rewriterKey = null;
    }

    /**
     * Checks whether the Rewriter API exists in the current runtime
     * This does not guarantee the model is downloadable/usable
     * @returns {boolean}
     */
    isAvailable() {
      return typeof self !== "undefined" && "Rewriter" in self;
    }

    /**
     * Rewrite text using Chrome built-in Rewriter
     * @param {*} text
     * @param {*} options
     * @returns {Promise<string|null>}
     */
    async rewrite(text, options = {}) {
      const preparedText = this._prepareRewriteInput(text);
      if (preparedText === null) {
        return null;
      }

      const rewriter = await this._getOrCreateRewriter(options);
      if (!rewriter) {
        throw new Error("Chrome Rewriter API not available");
      }

      const contextText = this._formatContextForm(options.contextForm);
      let contextInstruction = "";
      if (contextText) {
        contextInstruction = `${context.ThemeService.getValue("gbc-ai-rewrite-chrome-rewriter-instructions")}\n${contextText}`;
      }

      return await rewriter.rewrite(preparedText, { context: contextInstruction });
    }

    /**
     * Returns the cached Chrome Rewriter instance, or lazily creates one
     * @private
     * @param {Object} [options={}] Rewriter creation options
     * @returns {Promise<any|null>} A Rewriter instance, or null when not usable
     */
    async _getOrCreateRewriter(options = {}) {
      if (!this.isAvailable()) {
        throw new Error("AIChromeRewriterProvider: Rewriter API not supported in this browser/context");
      }

      const createOptions = this._normalizeCreateOptions(options);
      const key = this._getCreateOptionsKey(createOptions);

      if (this._rewriter && this._rewriterKey === key) {
        return this._rewriter;
      }
      if (this._rewriterPromise) {
        return this._rewriterPromise;
      }

      if (this._rewriter && this._rewriterKey !== key) {
        this._rewriter.destroy();
        this._rewriter = null;
        this._rewriterKey = null;
      }

      const availability = await self.Rewriter.availability({ outputLanguage: createOptions.outputLanguage });
      if (availability === "unavailable") {
        throw new Error("AIChromeRewriterProvider: Rewriter API not available");
      }

      this._rewriterPromise = self.Rewriter.create(createOptions)
        .then((rw) => {
          this._rewriter = rw;
          this._rewriterKey = key;
          return rw;
        })
        .finally(() => {
          this._rewriterPromise = null;
        });

      return this._rewriterPromise;
    }

    /**
     * Builds a stable cache key for Rewriter.create options
     * @param {Object} createOptions
     * @returns {string}
     * @private
     */
    _getCreateOptionsKey(createOptions) {
      return [createOptions.tone, createOptions.format, createOptions.length, createOptions.outputLanguage || ""].join("|");
    }

    /**
     * Normalizes user options into a complete Rewriter.create() options object
     * @param {Object} options
     * @returns {Object}
     */
    _normalizeCreateOptions(options = {}) {
      const createOptions = {
        tone: options.tone || "as-is",
        format: options.format || "plain-text",
        length: options.length || "as-is",
        outputLanguage: options.outputLanguage || "en"
      };

      return createOptions;
    }

    /**
     * @inheritDoc
     */
    destroy() {
      if (this._rewriter) {
        this._rewriter.destroy();
      }
      this._rewriter = null;
      this._rewriterPromise = null;
      this._rewriterKey = null;
    }
  }

  cls.AIProviderBase.registerProvider(ChromeRewriterProvider);
  cls.ChromeRewriterProvider = ChromeRewriterProvider;
});
